import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiPhone, FiBookOpen, FiAward, FiMessageSquare, FiSend, FiCheckCircle, FiDollarSign, FiClock, FiUsers, FiTrendingUp } = FiIcons;

const BecomeTutor = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    whatsapp: '',
    qualification: '',
    subjects: '',
    experience: '',
    availability: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email via Cloudflare Pages Function
      const response = await fetch('/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'become-tutor',
          formData: formData
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          whatsapp: '',
          qualification: '',
          subjects: '',
          experience: '',
          availability: '',
          message: ''
        });
        
        alert('✅ Your tutor application has been sent successfully! We\'ll contact you within 24 hours. Check your email for confirmation.');
      } else {
        throw new Error(result.error || 'Failed to send application');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('⚠️ There was an error sending your application. Please try again or contact us at info@tutorhubonline.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const qualifications = [
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'PhD/Doctorate',
    'Professional Certification',
    'Teaching Certificate',
    'Other (Please specify in message)'
  ];

  const experienceOptions = [
    'Less than 1 year',
    '1-2 years',
    '3-5 years',
    '5+ years',
    'New to tutoring but have subject expertise'
  ];

  const availabilityOptions = [
    'Weekdays only',
    'Weekends only',
    'Both weekdays and weekends',
    'Flexible schedule',
    'Part-time (few hours per week)',
    'Full-time availability'
  ];

  const benefits = [
    {
      icon: FiDollarSign,
      title: 'Competitive Pay',
      description: 'Earn attractive hourly rates based on your expertise and experience'
    },
    {
      icon: FiClock,
      title: 'Flexible Hours',
      description: 'Set your own schedule and work from anywhere in the world'
    },
    {
      icon: FiUsers,
      title: 'Global Students',
      description: 'Teach students from different countries and cultures'
    },
    {
      icon: FiTrendingUp,
      title: 'Career Growth',
      description: 'Develop your teaching skills and build a successful tutoring career'
    }
  ];

  const requirements = [
    'Strong academic background in your subject area',
    'Excellent communication skills in English',
    'Reliable internet connection for online sessions',
    'Passion for teaching and helping students succeed',
    'Patience and ability to explain complex concepts clearly',
    'Professional attitude and commitment to student success'
  ];

  const subjects = [
    'Mathematics & Statistics',
    'Physics',
    'Chemistry',
    'Biology',
    'Business Management',
    'Economics',
    'Accounting & Finance',
    'Computer Science',
    'Engineering',
    'English & Literature',
    'Psychology',
    'Sociology',
    'History',
    'Data Analysis',
    'Other (Please specify)'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-blue-600 to-blue-800 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-blue-600/80 to-blue-800/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Become a Tutor at Tutor Hub Online
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              Join our team of expert tutors and help students worldwide achieve their academic goals. Share your knowledge, earn competitive pay, and make a difference in students' lives.
            </p>
            <div className="inline-flex items-center bg-accent/20 backdrop-blur-sm text-accent px-6 py-3 rounded-full border border-accent/30">
              <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2" />
              <span className="font-semibold">Now Hiring Qualified Tutors</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Why Teach with Us?</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Join a platform that values your expertise and provides excellent opportunities for professional growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Requirements Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-16 bg-muted"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">What We're Looking For</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We seek passionate educators who are committed to student success and academic excellence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {requirements.map((requirement, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex items-start space-x-3"
              >
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{requirement}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Application Form Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-20"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Apply to Become a Tutor</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Fill out the application form below and we'll contact you via WhatsApp to discuss opportunities.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-xl p-8 border">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiUser} className="inline w-4 h-4 mr-2 text-primary" />
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="Enter your full name"
                />
              </motion.div>

              {/* Email Address */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiMail} className="inline w-4 h-4 mr-2 text-primary" />
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="your.email@example.com"
                />
              </motion.div>

              {/* WhatsApp Number */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <label htmlFor="whatsapp" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiPhone} className="inline w-4 h-4 mr-2 text-primary" />
                  WhatsApp Number *
                </label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  required
                  value={formData.whatsapp}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                  placeholder="+44 7440 373308"
                />
              </motion.div>

              {/* Highest Qualification */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <label htmlFor="qualification" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiAward} className="inline w-4 h-4 mr-2 text-primary" />
                  Highest Qualification *
                </label>
                <select
                  id="qualification"
                  name="qualification"
                  required
                  value={formData.qualification}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                >
                  <option value="">Select your highest qualification...</option>
                  {qualifications.map((qualification, index) => (
                    <option key={index} value={qualification}>
                      {qualification}
                    </option>
                  ))}
                </select>
              </motion.div>

              {/* Subjects */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <label htmlFor="subjects" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiBookOpen} className="inline w-4 h-4 mr-2 text-primary" />
                  Subjects You Can Teach *
                </label>
                <textarea
                  id="subjects"
                  name="subjects"
                  required
                  rows={3}
                  value={formData.subjects}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 resize-none"
                  placeholder="List the subjects you can teach (e.g., Mathematics, Physics, Business Management, etc.)"
                ></textarea>
              </motion.div>

              {/* Teaching Experience */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiUsers} className="inline w-4 h-4 mr-2 text-primary" />
                  Teaching/Tutoring Experience *
                </label>
                <select
                  id="experience"
                  name="experience"
                  required
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                >
                  <option value="">Select your experience level...</option>
                  {experienceOptions.map((experience, index) => (
                    <option key={index} value={experience}>
                      {experience}
                    </option>
                  ))}
                </select>
              </motion.div>

              {/* Availability */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <label htmlFor="availability" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiClock} className="inline w-4 h-4 mr-2 text-primary" />
                  Availability *
                </label>
                <select
                  id="availability"
                  name="availability"
                  required
                  value={formData.availability}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300"
                >
                  <option value="">Select your availability...</option>
                  {availabilityOptions.map((availability, index) => (
                    <option key={index} value={availability}>
                      {availability}
                    </option>
                  ))}
                </select>
              </motion.div>

              {/* Additional Message */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiMessageSquare} className="inline w-4 h-4 mr-2 text-primary" />
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 resize-none"
                  placeholder="Tell us about your teaching philosophy, specializations, or any other relevant information..."
                ></textarea>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="text-center pt-4"
              >
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full sm:w-auto bg-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center justify-center text-lg ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </motion.button>
              </motion.div>
            </form>
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="py-16 bg-primary text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-blue-600 to-blue-800" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 variants={itemVariants} className="text-3xl lg:text-4xl font-bold mb-4">
            🎯 Ready to Start Your Tutoring Journey?
          </motion.h2>
          <motion.p variants={itemVariants} className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join our community of expert tutors and help students worldwide achieve their academic goals while building a rewarding career.
          </motion.p>
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="https://wa.me/447440373308"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-accent text-black px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
            >
              💬 WhatsApp: +44 7440 373308
            </motion.a>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default BecomeTutor;